import {
  Box,
  CircularProgress,
  IconButton,
  Modal,
  OutlinedInput,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "assets/svg/CheckCirleIcon";
import CloseIcon from "assets/svg/CloseIcon";
import { AxiosError } from "axios";
import PrimaryButton from "components/PrimaryButton/PrimaryButton";
import { commonStyle } from "pages/Dashboard/common.styles";
import React, { useState } from "react";
import { toast } from "react-toastify";
import {
  AccountActivateRequest,
  UpdateEmailAddressRequest,
} from "reduxStore/dataTypes";
import { updateUserEmailAddress } from "services/apiService";
import theme, { responsiveFontSize } from "themes/Theme";
import VerifyEmailOTP from "components/VerifyEmailOTP/VerifyEmailOTP";

interface ModalProps {
  existingEmail: string;
  open: boolean;
  handleClose: () => void;
  onEmailUpdateSuccess: () => void;
}

const ChangeEmailAddress: React.FC<ModalProps> = ({
  open,
  handleClose,
  existingEmail,
  onEmailUpdateSuccess,
}) => {
  const [newEmailAddress, setNewEmailAddress] = useState<string | null>(null);
  const [newEmailAddressError, setNewEmailAddressError] = useState<
    string | null
  >(null);
  const [loading, setLoading] = useState(false);
  const [openOtpModal, setOpenOtpModal] = useState(false);
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const handleOpenOtpModal = () => setOpenOtpModal(true);
  const handleCloseOtpModal = () => setOpenOtpModal(false);

  const requestEmailUpdate = async () => {
    try {
      const body: UpdateEmailAddressRequest = {
        email: newEmailAddress || "",
      };
      setLoading(true);
      const response = await updateUserEmailAddress(body);
      if (response && response.status === 200) {
        toast.success(response?.data?.message);
        handleOpenOtpModal();
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      if (error instanceof AxiosError) {
        toast.error(error.response?.data?.message);
      }
    }
  };

  const handleOtpSuccess = () => {
    setNewEmailAddress("");
    setNewEmailAddressError("");
    toast.success("Email address updated successfully");
    handleCloseOtpModal();
    handleClose();
    onEmailUpdateSuccess();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="change-email-modal"
      aria-describedby="change-email-description"
    >
      <React.Fragment>
        <Box sx={styles.modalContainer}>
          <Box sx={styles.modalHeader}>
            <Typography sx={styles.modalTitle}>Change email address</Typography>
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Typography sx={styles.stepLabelText} mt={1}>
            Enter your new email address to replace <span>{existingEmail}</span>
            .
          </Typography>
          <TextField
            type="text"
            margin="dense"
            variant="outlined"
            size="small"
            fullWidth
            placeholder="Enter new email address"
            value={newEmailAddress}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              if (e.target.value) {
                setNewEmailAddressError(null);
              }
              setNewEmailAddress(e.target.value);
            }}
            slotProps={commonStyle.inputSlotTextStyle}
            error={newEmailAddressError === null ? false : true}
            helperText={newEmailAddressError || "" || undefined}
          />
          {loading ? (
            <CircularProgress sx={{ mt: 1 }} />
          ) : (
            <PrimaryButton
              title="Update Email Address"
              viewStyle={{ mt: 1 }}
              onClick={() => {
                if (
                  newEmailAddress === "" ||
                  newEmailAddress === null ||
                  newEmailAddress === undefined
                ) {
                  setNewEmailAddressError("Please enter new email address");
                } else if (!regex.test(newEmailAddress)) {
                  setNewEmailAddressError("Please enter a valid email address");
                } else {
                  requestEmailUpdate();
                }
              }}
            />
          )}
        </Box>
        <VerifyEmailOTP
          open={openOtpModal}
          handleClose={handleCloseOtpModal}
          onSuccess={handleOtpSuccess}
          email={newEmailAddress}
        />
      </React.Fragment>
    </Modal>
  );
};
export default ChangeEmailAddress;
const styles = {
  modalContainer: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    minWidth: 400,
    maxWidth: { xs: window.innerWidth * 0.8, md: window.innerWidth * 0.5 },
    bgcolor: "#FFFFFF",
    borderRadius: "2em",
    px: 4,
    py: 2,
  },
  modalTitle: {
    fontSize: responsiveFontSize(16),
    color: theme.palette.primary.main,
    fontWeight: 400,
  },
  modalHeader: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  stepNumber: {
    backgroundColor: theme.palette.primary.main,
    borderRadius: "50%",
    width: "1.2em",
    height: "1.2em",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  stepNumberText: {
    fontSize: responsiveFontSize(12),
    fontWeight: 400,
    color: "#FFFFFF",
  },
  stepLabelText: {
    fontSize: responsiveFontSize(14),
    fontWeight: 400,
    color: theme.palette.text.primary,
    "& span": {
      color: theme.palette.primary.main,
    },
  },
};
